import { ERROR_MSG_CART_FETCH, handleError } from "@/helpers/errors";
import { fetchJson, fetchOk, postJson, queryClient } from "@/helpers/fetch";
import { Money } from "@/helpers/Money";
import { LiquidSellingPlanPriceAdjustment } from "@/liquid";
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { productSelect, variantSelect, getSizeInfo } from "@/store";
import { Overwrite } from "@/types/utility";
import { track } from "@/helpers/googleAnalytics";
import { getActiveTests } from "@/helpers/growthbook";

export type ShopifyCartRaw = {
  attributes: Record<string, string | null>;
  currency: string;
  token: string;
  items: ShopifyCartItemRaw[];
  item_count: number;
  total_price: number;
  items_subtotal_price: number;
  total_weight: number;
};

export type ShopifyCartItemRaw = {
  discounted_price: number;
  discounts: number[];
  final_line_price: number;
  final_price: number;
  gift_card: boolean;
  options_with_values: {
    name: string;
    value: string | number;
  }[];
  grams: number;
  handle: string;
  id: number;
  image?: string;
  key: string;
  line_level_discount_allocations: number[];
  line_level_total_discount: number;
  line_price: number;
  original_line_price: number;
  original_price: number;
  price: number;
  product_description: string;
  product_id: number;
  product_title: string;
  product_type: string;
  properties: { [key: string]: number | string | boolean } | null;
  quantity: number;
  requires_shipping: boolean;
  sku: string;
  taxable: boolean;
  title: string;
  total_discount: number;
  selling_plan_allocation?: {
    selling_plan: {
      id: number;
      name: string;
      price_adjustments?: LiquidSellingPlanPriceAdjustment[];
    };
    compare_at_price: number;
    per_delivery_price: number;
  };
  url: string;
  variant_id: number;
  variant_options: string[];
  variant_title: string;
  metafields: any;
};

export type ShopifyCart = Overwrite<
  ShopifyCartRaw,
  {
    total_price: Money;
    items_subtotal_price: Money;
    items: ShopifyCartItem[];
  }
>;

export type ShopifyCartItem = Overwrite<
  ShopifyCartItemRaw,
  {
    discounted_price: Money;
    final_line_price: Money;
    final_price: Money;
    line_level_total_discount: Money;
    line_price: Money;
    original_line_price: Money;
    original_price: Money;
    price: Money;
  }
>;

export const shopifyCartApiKeys = {
  all: ["cart"] as const,
  fetch: () => [...shopifyCartApiKeys.all, "fetch"],
  change: () => [...shopifyCartApiKeys.all, "change"],
  add: () => [...shopifyCartApiKeys.all, "add"],
};

export const shopifyCartApi = {
  fetch: () => fetchJson<ShopifyCartRaw>("/cart.js"),
  useFetch: () =>
    useQuery({
      queryKey: shopifyCartApiKeys.fetch(),
      queryFn: shopifyCartApi.fetch,
      onError: (e) => handleError(e, ERROR_MSG_CART_FETCH),
      staleTime: 1000,
      select: (data): ShopifyCart => ({
        ...data,
        total_price: Money.from(data.total_price, "¢"),
        items_subtotal_price: Money.from(data.items_subtotal_price, "¢"),
        items: data.items
          .filter((x) => x.quantity > 0)
          .map((x: ShopifyCartItemRaw) => ({
            ...x,
            discounted_price: Money.from(x.discounted_price, "¢"),
            final_line_price: Money.from(x.final_line_price, "¢"),
            final_price: Money.from(x.final_price, "¢"),
            line_level_total_discount: Money.from(
              x.line_level_total_discount,
              "¢"
            ),
            line_price: Money.from(x.line_price, "¢"),
            original_line_price: Money.from(x.original_line_price, "¢"),
            original_price: Money.from(x.original_price, "¢"),
            price: Money.from(x.price, "¢"),
          }))
          .reverse(),
      }),
    }),

  change: (body: CartChangeBody) =>
    postJson<ShopifyCartRaw>("/cart/change.js", { body }),
  useChange() {
    const queryClient = useQueryClient();
    const qk = shopifyCartApiKeys.fetch();
    return useMutation({
      mutationKey: shopifyCartApiKeys.change(),
      mutationFn: shopifyCartApi.change,
      onMutate: async (change: CartChangeBody) => {
        track({
          name: "Add Item Info",
          sourceModule: "cart_update",
        });
        // Optimistically apply cart item mutations
        // https://tanstack.com/query/v4/docs/react/guides/optimistic-updates
        await queryClient.cancelQueries(qk);
        const prev = queryClient.getQueryData<ShopifyCartRaw>(qk);
        if (!prev) {
          return;
        }

        const next = { ...prev, items: prev.items.slice() };
        const itemIdx = next.items.findIndex((x) => x.key == change.id);
        if (itemIdx > -1) {
          const item: ShopifyCartItemRaw = next.items[itemIdx];
          let newSellingPlan = item.selling_plan_allocation;
          if (change.selling_plan === null || change.selling_plan) {
            newSellingPlan =
              change.selling_plan === null
                ? undefined
                : {
                    selling_plan: {
                      id: change.selling_plan,
                      name:
                        item.selling_plan_allocation?.selling_plan.name || "",
                    },
                    compare_at_price:
                      item.selling_plan_allocation?.compare_at_price || 0,
                    per_delivery_price:
                      item.selling_plan_allocation?.per_delivery_price || 0,
                  };
          }
          const diffQuantity = change.quantity - item.quantity;
          next.item_count += diffQuantity;
          next.total_price += item.price * diffQuantity;
          next.items_subtotal_price += item.price * diffQuantity;
          next.total_weight += item.grams * diffQuantity;
          next.items[itemIdx] = {
            ...item,
            final_line_price:
              item.final_line_price + item.final_price * diffQuantity,
            line_price: item.line_price + item.price * diffQuantity,
            original_line_price:
              item.original_line_price + item.original_price * diffQuantity,
            quantity: change.quantity,
            selling_plan_allocation: newSellingPlan,
          } as ShopifyCartItemRaw;
        }
        queryClient.setQueryData<ShopifyCartRaw>(qk, next);
        return { prev, next };
      },
      onSuccess: (data) => {
        // If there are other cart changes in flight, we could potentially
        // overwrite another optimistic update which would be more accurate.
        const filter = { mutationKey: shopifyCartApiKeys.change() };
        if (queryClient.isMutating(filter) <= 1) {
          queryClient.setQueryData(qk, data);
        }
      },
      onError: (err, _change, context) => {
        handleError(err, "cart update failed");
        queryClient.setQueryData(qk, context?.prev);
        queryClient.invalidateQueries(qk);
      },
    });
  },

  clear: () => fetchJson<ShopifyCartRaw>("/cart/clear.js", { method: "POST" }),

  add: (body: CartAddBody) => {
    const sourceModule = "";
    return shopifyCartApi.trackedAdd({ ...body, sourceModule });
  },

  useAdd: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: shopifyCartApiKeys.add(),
      mutationFn: shopifyCartApi.add,
      onSuccess: () => {
        queryClient.invalidateQueries(shopifyCartApiKeys.all);
      },
      onError: (e) => handleError(e, "cart add failed"),
    });
  },

  trackedAdd: (body: CartAddBodyWithTracking) => {
    const sourceModule = body.sourceModule;
    setAbVariantsOnAddToCart(body);
    if (sourceModule) {
      setSourceModuleOnAddToCart(body, sourceModule);
      track({
        name: "Add Item Info",
        sourceModule,
      });
    }
    if (body instanceof FormData) {
      return fetchJson<CartAddResponse>("/cart/add.js", {
        method: "POST",
        body,
      });
    } else {
      return postJson<CartAddResponse>("/cart/add.js", { body });
    }
  },

  useTrackedAdd: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: shopifyCartApiKeys.add(),
      mutationFn: shopifyCartApi.trackedAdd,
      onSuccess: () => {
        queryClient.invalidateQueries(shopifyCartApiKeys.all);
      },
      onError: (e) => handleError(e, "cart add failed"),
    });
  },
} as const;

type CartChangeBody = {
  id: ShopifyCartItemRaw["key"];
  quantity: number;
  selling_plan?: number | null;
};

const setSourceModuleOnAddToCart = (
  body: CartAddBody,
  sourceModule: string
) => {
  if (body instanceof FormData) {
    body.append(`properties[_source-module]`, sourceModule);
  } else {
    body.items = body.items.map((item) => ({
      ...item,
      properties: {
        ...item.properties,
        "_source-module": sourceModule,
      },
    }));
  }
};

const setAbVariantsOnAddToCart = (body: CartAddBody) => {
  const abEntries = Object.entries(getActiveTests());

  if (body instanceof FormData) {
    for (const [key, val] of abEntries) {
      body.append(`properties[_optimize-${key}]`, val.toString());
    }
  } else {
    const optimizeProps = Object.fromEntries(
      abEntries.map(([key, val]) => [`_optimize-${key}`, val])
    );
    body.items = body.items.map((item) => ({
      ...item,
      properties: {
        ...item.properties,
        ...optimizeProps,
      },
    }));
  }
};

export type CartAddLineItem = {
  id: ShopifyCartItemRaw["variant_id"];
  quantity: number;
  properties?: Record<string, string | number | boolean> | null;
  selling_plan?: number;
};

type CartAddBodyJson = {
  items: CartAddLineItem[];
};

export type CartAddBody = CartAddBodyJson | FormData;
export type CartAddBodyWithTracking = CartAddBody & {
  sourceModule: string;
};
type CartAddResponse = Pick<ShopifyCartRaw, "items">;

const newDeferredPromise = () => {
  let resolve = (_value: unknown) => {};
  const promise = new Promise((res) => {
    resolve = res;
  });
  return { promise, resolve };
};

export const useRedirectToCheckout = () => {
  const isCartMutating = useCartIsMutating();
  const finishedMutationsRef = useRef(newDeferredPromise());

  // allows for us to wait until there are no cart mutations in flight
  useEffect(() => {
    if (!isCartMutating) {
      finishedMutationsRef.current.resolve(void 0);
    } else {
      finishedMutationsRef.current = newDeferredPromise();
    }
  }, [isCartMutating]);

  return useCallback(async () => {
    await finishedMutationsRef.current.promise;
    window.location.href = "/checkout";
  }, []);
};

export const setCartAndCheckout = async (
  items: CartAddBodyJson["items"],
  sourceModule: string
): Promise<void> => {
  await shopifyCartApi.clear();
  await shopifyCartApi.trackedAdd({ items, sourceModule });
  queryClient.invalidateQueries(shopifyCartApiKeys.all);
  window.location.href = "/checkout";
};

export const setCart = async (
  items: CartAddBodyJson["items"]
): Promise<void> => {
  await shopifyCartApi.clear();
  await shopifyCartApi.add({ items });
};

export type CartItemSubscription =
  | {
      kind: "none";
    }
  | {
      kind: "selling-plan";
      id: number;
      name: string;
    };

export const cartSelect = {
  containsSubscription: (cart: ShopifyCart) =>
    cart.items.some(
      (x) => cartItemSelect.subscription(x).kind == "selling-plan"
    ),
};

export const cartItemSelect = {
  subscription: (item: ShopifyCartItem) => {
    if (item.selling_plan_allocation?.selling_plan) {
      return {
        kind: "selling-plan",
        id: item.selling_plan_allocation.selling_plan.id,
        name: item.selling_plan_allocation.selling_plan.name,
      };
    }
    return { kind: "none" };
  },
  productUrl: (item: ShopifyCartItem) => productSelect.url(item),
  sizing: (item: ShopifyCartItem) =>
    variantSelect.sizeFormat({ size: getSizeInfo(item.variant_options) }),
};

export const useCartHasPessimisticMutations = () =>
  useIsMutating(shopifyCartApiKeys.add()) > 0;
export const useCartIsMutating = () =>
  useIsMutating(shopifyCartApiKeys.all) > 0;
